'use client'

import { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'

import { Spinner } from '@/app/components/teqplay-ui'
import { TVesselJourneyResponse } from '@/api/v1/resources/vesselJourney'
import { getPortConfig, type TLocalizeVersion } from '@/app/config/ports'
import {
  STATIC_MAP_WIDTH,
  STATIC_MAP_HEIGHT,
  buildStaticMapUrl,
  getCircleRadiusPx,
  resolveMapView,
  resolveVesselPosition
} from '@/app/utils/staticMap'
import './Map.scss'

const RADIUS_KM = 1
const DEFAULT_ZOOM = 2

type TProps = {
  vessel: TVesselJourneyResponse
  localizeVersion?: TLocalizeVersion
}

function Map({ vessel, localizeVersion }: TProps) {
  const t = useTranslations('Map')
  const mapToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN ?? ''

  const hasValidLocation = vessel.ship.location?.lat != null && vessel.ship.location?.lon != null

  const portConfig = localizeVersion ? getPortConfig(localizeVersion) : null

  const vesselLon = hasValidLocation ? vessel.ship.location!.lon : null
  const vesselLat = hasValidLocation ? vessel.ship.location!.lat : null

  const {
    lon: mapLon,
    lat: mapLat,
    zoom: mapZoom
  } = resolveMapView(vesselLon, vesselLat, portConfig, DEFAULT_ZOOM)

  const imageUrl = hasValidLocation ? buildStaticMapUrl(mapLon, mapLat, mapZoom, mapToken) : null

  const [isImageLoading, setIsImageLoading] = useState(true)

  useEffect(() => {
    if (imageUrl) {
      setIsImageLoading(true)
    }
  }, [imageUrl])

  if (!mapToken) {
    return (
      <div className="map-error">
        <strong>Error:</strong> Mapbox access token is missing. Please set the{' '}
        <code>NEXT_PUBLIC_MAPBOX_TOKEN</code> environment variable.
      </div>
    )
  }

  const vesselPos = hasValidLocation
    ? resolveVesselPosition(mapLon, mapLat, vesselLon!, vesselLat!, mapZoom, portConfig != null)
    : null

  const circleRadiusPx = hasValidLocation ? getCircleRadiusPx(vesselLat!, RADIUS_KM, mapZoom) : null

  return (
    <div className="map-container">
      {imageUrl ? (
        <>
          {isImageLoading && (
            <div className="map-loading-overlay">
              <Spinner loading size="l" />
            </div>
          )}

          <img
            className="map-static-image"
            src={imageUrl}
            alt="Vessel location map"
            draggable={false}
            onLoad={() => setIsImageLoading(false)}
            onError={() => setIsImageLoading(false)}
          />

          {/* Dashed circle indicating approximate vessel area*/}
          {circleRadiusPx !== null && vesselPos !== null && (
            <svg
              className="map-static-overlay"
              viewBox={`0 0 ${STATIC_MAP_WIDTH} ${STATIC_MAP_HEIGHT}`}
              aria-hidden
            >
              <circle
                cx={vesselPos.x}
                cy={vesselPos.y}
                r={circleRadiusPx}
                fill="rgba(23, 47, 133, 0.2)"
                stroke="#172f85"
                strokeWidth="1"
                strokeDasharray="3 2"
              />
            </svg>
          )}
        </>
      ) : null}
      {/* Location unavailable overlay */}
      {!hasValidLocation && (
        <div className="location-unavailable-overlay">
          <div className="location-unavailable-message">
            <strong>{t('locationUnavailable')}</strong>
            <p>{t('locationUnavailableDescription')}</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default Map
